import { useEffect, useReducer, useRef } from 'react';
import mqtt from 'mqtt';

// Reducer keeps a flat list capped at `limit`, newest first. New spots
// dedupe on `id` so MQTT echoes (we get the same spot from two upstreams)
// don't double-fill the table.
function reducer(state, action) {
  switch (action.type) {
    case 'seed': {
      const seen = new Set();
      const out = [];
      for (const s of action.spots) {
        if (seen.has(s.id)) continue;
        seen.add(s.id);
        out.push(s);
      }
      return out.slice(0, action.limit);
    }
    case 'push': {
      const sp = action.spot;
      // Already in the list? Keep position.
      if (state.some((s) => s.id === sp.id)) return state;
      const next = [sp, ...state];
      return next.slice(0, action.limit);
    }
    case 'limit': {
      return state.slice(0, action.limit);
    }
    case 'locator': {
      // Update DX coords for any spot whose dx_callsign matches.
      const cl = action.locator;
      let changed = false;
      const next = state.map((s) => {
        if (s.dx_callsign === cl.call && (s.dx_lat !== cl.lat || s.dx_lon !== cl.lon)) {
          changed = true;
          return { ...s, dx_lat: cl.lat, dx_lon: cl.lon, dx_locator: cl.locator || s.dx_locator };
        }
        return s;
      });
      return changed ? next : state;
    }
    default:
      return state;
  }
}

// useSpots: maintain the live spot store seeded from /api/spots and updated
// from MQTT. Returns `{ spots, locators, status }`.
export function useSpots({ limit }) {
  const [spots, dispatch] = useReducer(reducer, []);
  const locatorsRef = useRef(new Map());
  const statusRef = useRef({ connected: false, error: null });
  const [, setTick] = useReducer((x) => x + 1, 0);

  // Seed from REST.
  useEffect(() => {
    let aborted = false;
    fetch(`/api/spots?limit=${limit}`)
      .then((r) => r.json())
      .then((j) => {
        if (aborted) return;
        dispatch({ type: 'seed', spots: j.spots || [], limit });
      })
      .catch((e) => console.error('seed failed', e));
    return () => {
      aborted = true;
    };
  }, [limit]);

  // Apply limit changes without re-fetch.
  useEffect(() => {
    dispatch({ type: 'limit', limit });
  }, [limit]);

  // Subscribe to MQTT.
  useEffect(() => {
    const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const url = `${proto}://${window.location.host}/mqtt`;
    const client = mqtt.connect(url, {
      clientId: `web-${Math.random().toString(16).slice(2, 10)}`,
      reconnectPeriod: 5000,
    });

    client.on('connect', () => {
      statusRef.current = { connected: true, error: null };
      setTick();
      client.subscribe('dxspider/spot/#');
      client.subscribe('dxspider/locator/#');
    });
    client.on('message', (topic, payload) => {
      try {
        const msg = JSON.parse(payload.toString());
        if (topic.startsWith('dxspider/spot/')) {
          dispatch({ type: 'push', spot: msg, limit });
        } else if (topic.startsWith('dxspider/locator/')) {
          locatorsRef.current.set(msg.call, msg);
          dispatch({ type: 'locator', locator: msg });
        }
      } catch (e) {
        console.error('mqtt parse', e);
      }
    });
    client.on('close', () => {
      statusRef.current = { ...statusRef.current, connected: false };
      setTick();
    });
    client.on('error', (err) => {
      statusRef.current = { ...statusRef.current, error: err.message };
      setTick();
    });
    return () => client.end(true);
  }, [limit]);

  return { spots, locators: locatorsRef.current, status: statusRef.current };
}
